// pages/Items.js - Updated with Stock Management

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, push, onValue, remove, update, get } from "firebase/database";
import Barcode from "react-barcode";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from "react-router-dom";
import { hsnMasterData } from "../data/hsnDatabase";
import "../styles/main.css";

export default function Items() {

  const navigate = useNavigate();

  const [items, setItems] = useState([]);

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [gst, setGst] = useState("18");
  const [hsn, setHsn] = useState("");
  const [barcode, setBarcode] = useState("");
  const [unit, setUnit] = useState("NOS");
  const [category, setCategory] = useState("");
  const [minimumStock, setMinimumStock] = useState("0");
  const [maximumStock, setMaximumStock] = useState("0");
  const [currentStock, setCurrentStock] = useState("0");

  const [editId, setEditId] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [hsnSuggestions, setHsnSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hsnValid, setHsnValid] = useState(false);
  const [hsnMessage, setHsnMessage] = useState("");
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustmentQty, setAdjustmentQty] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("add");

  // Units list
  const units = ["NOS", "PCS", "KG", "GRAM", "LTR", "ML", "METER", "DOZEN", "BOX", "PACKET"];

  // Categories list
  const categories = [
    "Electronics", "Clothing", "Groceries", "Stationery", "Furniture",
    "Hardware", "Pharmacy", "Cosmetics", "Food", "Beverages", "Other"
  ];

  /* LOAD ITEMS */
  useEffect(() => {
    const itemRef = ref(database, "items");

    onValue(itemRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setItems([]);
        return;
      }

      const list = Object.keys(data).map((key) => ({
        id: key,
        ...data[key]
      }));

      // Sort by name
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setItems(list);
    });
  }, []);

  /* HSN VALIDATION & SEARCH */
  const handleHSNSearch = (value) => {
    setHsn(value);

    if (value.length < 2) {
      setShowSuggestions(false);
      setHsnValid(false);
      setHsnMessage("");
      return;
    }

    // Search in HSN master data
    const results = hsnMasterData.filter(item => {
      if (!item) return false;
      
      const hsnCode = item.hsn || '';
      const description = item.description || '';
      
      return hsnCode.startsWith(value) || 
             description.toLowerCase().includes(value.toLowerCase());
    });

    setHsnSuggestions(results.slice(0, 5));
    setShowSuggestions(true);

    // Check for exact match
    const exactMatch = hsnMasterData.find(item => item && item.hsn === value);

    if (exactMatch) {
      setHsnValid(true);
      setHsnMessage(`✓ Valid HSN: ${exactMatch.description}`);
      setGst(exactMatch.gst || "18");
    } else if (value.length >= 4 && !exactMatch) {
      setHsnValid(false);
      setHsnMessage("⚠ HSN not found in database");
    }
  };

  const selectHSN = (item) => {
    if (!item) return;

    setHsn(item.hsn || "");
    setGst(item.gst || "18");

    // Auto fill name if empty
    if (!name && item.description) {
      setName(item.description);
    }

    setHsnValid(true);
    setHsnMessage(`✓ Valid HSN: ${item.description}`);
    setShowSuggestions(false);
  };

  /* Auto-calculate selling price based on purchase price */
  const handlePurchasePriceChange = (value) => {
    setPurchasePrice(value);
    // Auto calculate selling price with 10% margin (can be adjusted)
    if (value && !sellingPrice) {
      const margin = parseFloat(value) * 1.1;
      setSellingPrice(Math.round(margin).toString());
    }
  };

  /* GENERATE BARCODE */
  const generateBarcode = () => {
    const code = Math.floor(
      100000000000 + Math.random() * 900000000000
    ).toString();
    setBarcode(code);
  };

  /* PRODUCT LOOKUP */
  const lookupProduct = async (code) => {
    try {
      const res = await fetch(
        `https://world.openfoodfacts.org/api/v0/product/${code}.json`
      );

      const data = await res.json();

      if (data.product && data.product.product_name) {
        setName(data.product.product_name);

        // Try to find matching HSN by product name
        const searchTerm = data.product.product_name.toLowerCase();
        const hsnMatch = hsnMasterData.find(item => 
          item && item.description && 
          item.description.toLowerCase().includes(searchTerm.substring(0, 15))
        );
        
        if (hsnMatch) {
          setHsn(hsnMatch.hsn);
          setGst(hsnMatch.gst);
          setHsnValid(true);
          setHsnMessage(`✓ Valid HSN: ${hsnMatch.description}`);
        }
      }
    } catch (err) {
      console.log("Product not found");
    }
  };

  /* START SCANNER */
  const startScanner = async () => {
    const htmlScanner = new Html5Qrcode("reader");

    setScanner(htmlScanner);

    await htmlScanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (barcodeValue) => {
        setBarcode(barcodeValue);
        lookupProduct(barcodeValue);
        htmlScanner.stop();
        setScanner(null);
      },
      (error) => {}
    );
  };

  /* CANCEL SCANNER */
  const cancelScanner = async () => {
    if (scanner) {
      await scanner.stop();
      setScanner(null);
    }
  };

  /* SAVE ITEM */
  const saveItem = async () => {
    if (!name) {
      alert("Enter item name");
      return;
    }

    // Validate HSN if entered
    if (hsn && hsn.length > 0) {
      const validHSN = hsnMasterData.find(item => item && item.hsn === hsn);
      if (!validHSN) {
        if (!window.confirm("HSN code not found in database. Continue anyway?")) {
          return;
        }
      }
    }

    const timestamp = new Date().toISOString();
    
    const itemData = {
      name,
      price: sellingPrice || price || "0",
      sellingPrice: sellingPrice || price || "0",
      purchasePrice: purchasePrice || "0",
      gst: gst || "18",
      hsn: hsn || "",
      barcode: barcode || "",
      unit: unit || "NOS",
      category: category || "Other",
      minimumStock: parseInt(minimumStock) || 0,
      maximumStock: parseInt(maximumStock) || 0,
      currentStock: currentStock ? parseInt(currentStock) : 0,
      updatedAt: timestamp
    };

    if (editId) {
      // Keep existing stock when updating
      const existingItem = items.find(i => i.id === editId);
      if (existingItem && !currentStock) {
        itemData.currentStock = existingItem.currentStock || 0;
      }
      await update(ref(database, "items/" + editId), itemData);
      alert("Item Updated");
      setEditId(null);
    } else {
      itemData.createdAt = timestamp;
      await push(ref(database, "items"), itemData);
      alert("Item Added");
    }

    // Clear form
    resetForm();
  };

  /* RESET FORM */
  const resetForm = () => {
    setName("");
    setPrice("");
    setPurchasePrice("");
    setSellingPrice("");
    setGst("18");
    setHsn("");
    setBarcode("");
    setUnit("NOS");
    setCategory("");
    setMinimumStock("0");
    setMaximumStock("0");
    setCurrentStock("0");
    setHsnValid(false);
    setHsnMessage("");
  };

  /* EDIT ITEM */
  const editItem = (item) => {
    setName(item.name || "");
    setPrice(item.sellingPrice || item.price || "");
    setPurchasePrice(item.purchasePrice || "");
    setSellingPrice(item.sellingPrice || item.price || "");
    setGst(item.gst || "18");
    setHsn(item.hsn || "");
    setBarcode(item.barcode || "");
    setUnit(item.unit || "NOS");
    setCategory(item.category || "Other");
    setMinimumStock(item.minimumStock?.toString() || "0");
    setMaximumStock(item.maximumStock?.toString() || "0");
    setCurrentStock(item.currentStock?.toString() || "0");

    // Validate HSN when editing
    if (item.hsn) {
      const validHSN = hsnMasterData.find(h => h && h.hsn === item.hsn);
      if (validHSN) {
        setHsnValid(true);
        setHsnMessage(`✓ Valid HSN: ${validHSN.description}`);
      }
    }

    setEditId(item.id);
  };

  /* DELETE ITEM */
  const deleteItem = (id) => {
    if (window.confirm("Delete item? This will also remove it from all invoices and purchases.")) {
      remove(ref(database, "items/" + id));
    }
  };

  /* ADJUST STOCK */
  const adjustStock = async () => {
    if (!selectedItem) return;
    if (!adjustmentQty || parseFloat(adjustmentQty) <= 0) {
      alert("Please enter a valid quantity");
      return;
    }

    const qty = parseFloat(adjustmentQty);
    const currentStockValue = selectedItem.currentStock || 0;
    let newStock;

    if (adjustmentType === "add") {
      newStock = currentStockValue + qty;
    } else if (adjustmentType === "remove") {
      if (qty > currentStockValue) {
        alert("Cannot remove more than current stock");
        return;
      }
      newStock = currentStockValue - qty;
    } else {
      newStock = qty;
    }

    try {
      await update(ref(database, `items/${selectedItem.id}`), {
        currentStock: newStock,
        lastStockUpdate: new Date().toISOString()
      });
      alert(`Stock updated! ${selectedItem.name} stock changed from ${currentStockValue} to ${newStock}`);
      setShowStockModal(false);
      setSelectedItem(null);
      setAdjustmentQty("");
    } catch (error) {
      console.error("Error updating stock:", error);
      alert("Failed to update stock");
    }
  };

  /* OPEN STOCK ADJUSTMENT MODAL */
  const openStockModal = (item) => {
    setSelectedItem(item);
    setAdjustmentType("add");
    setAdjustmentQty("");
    setShowStockModal(true);
  };

  return (
    <div className="mobile-app">

      {/* HEADER */}
      <div className="page-header">
        <h2>Items Management</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/stock")} className="stock-btn">
            📊 Stock
          </button>
        </div>
      </div>

      {/* FORM */}
      <div className="form">
        <input
          placeholder="Item Name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <div className="form-row">
          <input
            placeholder="Purchase Price"
            value={purchasePrice}
            onChange={(e) => handlePurchasePriceChange(e.target.value)}
            type="number"
            style={{ flex: 1 }}
          />
          <input
            placeholder="Selling Price"
            value={sellingPrice}
            onChange={(e) => setSellingPrice(e.target.value)}
            type="number"
            style={{ flex: 1 }}
          />
        </div>

        <div className="form-row">
          <select value={unit} onChange={(e) => setUnit(e.target.value)} style={{ flex: 1 }}>
            {units.map(u => (<option key={u} value={u}>{u}</option>))}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ flex: 1 }}>
            <option value="">Select Category</option>
            {categories.map(c => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>

        <select value={gst} onChange={(e) => setGst(e.target.value)}>
          <option value="0">GST 0%</option>
          <option value="5">GST 5%</option>
          <option value="12">GST 12%</option>
          <option value="18">GST 18%</option>
          <option value="28">GST 28%</option>
        </select>

        {/* HSN Input with Suggestions */}
        <div style={{ position: "relative" }}>
          <input
            placeholder="HSN Code (Type to search)"
            value={hsn}
            onChange={(e) => handleHSNSearch(e.target.value)}
            onFocus={() => hsn.length >= 2 && setShowSuggestions(true)}
            style={{
              borderColor: hsnValid ? '#4CAF50' : (hsn && !hsnValid ? '#ff4444' : ''),
              borderWidth: hsnValid || (hsn && !hsnValid) ? '2px' : '1px'
            }}
          />

          {hsnMessage && (
            <div style={{
              fontSize: "12px",
              marginTop: "5px",
              color: hsnValid ? '#4CAF50' : '#ff4444',
              padding: "2px 5px"
            }}>
              {hsnMessage}
            </div>
          )}

          {showSuggestions && hsnSuggestions.length > 0 && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "8px",
              zIndex: 1000,
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
            }}>
              {hsnSuggestions.map((item, index) => (
                <div
                  key={index}
                  onClick={() => selectHSN(item)}
                  style={{
                    padding: "10px",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                >
                  <b>{item.hsn}</b> - {item.description}
                  <br />
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    GST: {item.gst}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-row">
          <input
            placeholder="Minimum Stock"
            value={minimumStock}
            onChange={(e) => setMinimumStock(e.target.value)}
            type="number"
            style={{ flex: 1 }}
          />
          <input
            placeholder="Maximum Stock"
            value={maximumStock}
            onChange={(e) => setMaximumStock(e.target.value)}
            type="number"
            style={{ flex: 1 }}
          />
        </div>

        {editId && (
          <div className="form-row">
            <input
              placeholder="Current Stock"
              value={currentStock}
              onChange={(e) => setCurrentStock(e.target.value)}
              type="number"
              disabled
              style={{ flex: 1, background: "#f5f5f5" }}
            />
          </div>
        )}

        <input
          placeholder="Barcode"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
        />

        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={generateBarcode} style={{ flex: 1 }}>
            Generate Barcode
          </button>
          <button onClick={startScanner} style={{ flex: 1 }}>
            Scan Barcode
          </button>
        </div>

        {barcode && (
          <div style={{
            textAlign: "center",
            marginTop: "10px",
            padding: "10px",
            background: "#f9f9f9",
            borderRadius: "10px"
          }}>
            <Barcode
              value={barcode}
              format="CODE128"
              width={2}
              height={60}
            />
            <p style={{ fontSize: "12px", marginTop: "5px", color: "#666" }}>
              {barcode}
            </p>
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
          <button onClick={saveItem} style={{ flex: 2 }}>
            {editId ? "Update Item" : "Save Item"}
          </button>
          {editId && (
            <button onClick={resetForm} style={{ flex: 1, background: "#f0f0f0", color: "#333" }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* SCANNER */}
      <div style={{ position: "relative" }}>
        {scanner && (
          <button onClick={cancelScanner} className="cancel-scan">
            Cancel
          </button>
        )}
        <div id="reader"></div>
      </div>

      {/* ITEMS LIST */}
      <div className="section">
        <h3>Items List ({items.length})</h3>

        {items.length === 0 ? (
          <p style={{ textAlign: "center", color: "#999", padding: "20px" }}>
            No items added yet
          </p>
        ) : (
          items.map((item) => {
            const stockStatus = (item.currentStock || 0) <= (item.minimumStock || 0) && (item.currentStock || 0) > 0;
            const outOfStock = (item.currentStock || 0) === 0;
            
            return (
              <div className="section-card" key={item.id} style={{ borderLeft: outOfStock ? '4px solid #dc3545' : stockStatus ? '4px solid #ff9800' : '4px solid #28a745' }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px"
                }}>
                  <b style={{ fontSize: "16px" }}>{item.name}</b>
                  <button 
                    onClick={() => openStockModal(item)}
                    style={{
                      background: "#e3f2fd",
                      border: "none",
                      padding: "4px 10px",
                      borderRadius: "15px",
                      fontSize: "11px",
                      cursor: "pointer",
                      color: "#1976d2"
                    }}
                  >
                    🔄 Adjust Stock
                  </button>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <p style={{ fontSize: "18px", fontWeight: "bold", color: "#5a60ea" }}>
                    ₹ {item.sellingPrice || item.price}
                  </p>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ 
                      fontSize: "20px", 
                      fontWeight: "bold",
                      color: outOfStock ? "#dc3545" : stockStatus ? "#ff9800" : "#28a745"
                    }}>
                      {item.currentStock || 0}
                    </span>
                    <span style={{ fontSize: "11px", color: "#666" }}> {item.unit || "NOS"}</span>
                  </div>
                </div>

                <div style={{
                  display: "flex",
                  gap: "5px",
                  marginBottom: "10px",
                  flexWrap: "wrap"
                }}>
                  <span style={{
                    background: "#eef1ff",
                    padding: "3px 8px",
                    borderRadius: "12px",
                    fontSize: "11px"
                  }}>
                    GST {item.gst || 0}%
                  </span>
                  {item.hsn && (
                    <span style={{
                      background: "#e8f5e9",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      fontSize: "11px"
                    }}>
                      HSN {item.hsn}
                    </span>
                  )}
                  {item.category && item.category !== "Other" && (
                    <span style={{
                      background: "#fff3e0",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      fontSize: "11px"
                    }}>
                      {item.category}
                    </span>
                  )}
                  {outOfStock && (
                    <span style={{
                      background: "#ffebee",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      color: "#dc3545"
                    }}>
                      ❌ Out of Stock
                    </span>
                  )}
                  {stockStatus && !outOfStock && (
                    <span style={{
                      background: "#fff3e0",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      color: "#ff9800"
                    }}>
                      ⚠️ Low Stock
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => editItem(item)}
                    style={{
                      background: "#ffd84d",
                      color: "#333",
                      padding: "8px 15px",
                      borderRadius: "20px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      flex: 1
                    }}
                  >
                    ✏ Edit
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    style={{
                      background: "#ff5c5c",
                      color: "white",
                      padding: "8px 15px",
                      borderRadius: "20px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      flex: 1
                    }}
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* STOCK ADJUSTMENT MODAL */}
      {showStockModal && selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Adjust Stock: {selectedItem.name}</h3>
            <div className="current-stock">
              Current Stock: <strong>{selectedItem.currentStock || 0} {selectedItem.unit || "NOS"}</strong>
            </div>
            <div className="adjustment-type">
              <button className={adjustmentType === "add" ? "active" : ""} onClick={() => setAdjustmentType("add")}>➕ Add Stock</button>
              <button className={adjustmentType === "remove" ? "active" : ""} onClick={() => setAdjustmentType("remove")}>➖ Remove Stock</button>
              <button className={adjustmentType === "set" ? "active" : ""} onClick={() => setAdjustmentType("set")}>🔧 Set Stock</button>
            </div>
            <input
              type="number"
              placeholder={adjustmentType === "set" ? "New Stock Quantity" : "Quantity"}
              value={adjustmentQty}
              onChange={(e) => setAdjustmentQty(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={() => setShowStockModal(false)}>Cancel</button>
              <button onClick={adjustStock}>Apply</button>
            </div>
          </div>
        </div>
      )}

     {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>

      <style>{`
        .form-row {
          display: flex;
          gap: 10px;
          margin-bottom: 12px;
        }
        
        .stock-btn {
          background: #28a745;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          cursor: pointer;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 20px;
          width: 90%;
          max-width: 350px;
        }
        
        .modal-content h3 {
          margin: 0 0 15px 0;
        }
        
        .current-stock {
          background: #f5f5f5;
          padding: 10px;
          border-radius: 8px;
          margin-bottom: 15px;
          text-align: center;
        }
        
        .adjustment-type {
          display: flex;
          gap: 8px;
          margin-bottom: 15px;
        }
        
        .adjustment-type button {
          flex: 1;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background: white;
          cursor: pointer;
        }
        
        .adjustment-type button.active {
          background: #5a60ea;
          color: white;
          border-color: #5a60ea;
        }
        
        .modal-content input {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-bottom: 15px;
        }
        
        .modal-actions {
          display: flex;
          gap: 10px;
        }
        
        .modal-actions button {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        
        .modal-actions button:first-child {
          background: #f0f0f0;
        }
        
        .modal-actions button:last-child {
          background: #5a60ea;
          color: white;
        }
        
        .section-card {
          background: white;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 12px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
      `}</style>

    </div>
  );
}